import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import Field from '../../components/Field';
import Icon from '../../components/Icon';
import SchoolPicker from '../../components/SchoolPicker';
import StateCityFields, { EMPTY_ADDRESS, addressPayload, isOtherCity }
  from '../../components/StateCityFields';
import { ErrorBanner } from '../../components/Feedback';
import useForm from '../../hooks/useForm';
import * as v from '../../validation/rules';
import { STUDENT_REGISTRATION_CODES } from '../../validation/serverCodes';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

/**
 * Student registration, from FOCUS_Registration_Form.
 *
 * <p>One number, and it is the parent's — a student is not asked for a phone of their
 * own. Email is required and is what actually identifies a student: a parent's number is
 * shared between siblings by construction, an email address is not.
 *
 * <p>The school is not on the document and is asked for anyway — activation codes are
 * allocated to a school, so a student without one cannot be given a book.
 */
export default function StudentRegisterPage() {
  const { user, acceptAuth } = useAuth();
  const navigate = useNavigate();
  const [school, setSchool] = useState({ id: null, name: '' });
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [cityError, setCityError] = useState(null);
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      parentMobile: '',
      password: '',
      confirmPassword: '',
      area: '',
      postalCode: '',
    },
    validators: {
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      parentMobile: v.parentMobile,
      password: v.password,
      confirmPassword: v.confirmPassword,
      area: v.area,
      postalCode: v.postalCode,
    },
    crossField: v.passwordsMatch,
  });

  if (user) return <Navigate to="/" replace />;

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    // Picking "Other" and typing nothing is the one way this pair can be incomplete,
    // and the picker cannot see it — the choice is made in one field and answered in
    // another.
    if (isOtherCity(address) && !address.cityName.trim()) {
      setCityError('Type the name of your city');
      return;
    }
    setCityError(null);
    try {
      acceptAuth(
        await api.registerStudent({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim() || null,
          email: v.normalizeEmail(values.email),
          parentMobile: v.normalizeMobile(values.parentMobile),
          password: values.password,
          schoolId: school.id,
          schoolName: school.id ? null : school.name.trim() || null,
          area: values.area.trim() || null,
          ...addressPayload(address),
          postalCode: values.postalCode.trim() || null,
        }),
      );
      navigate('/', { replace: true });
    } catch (err) {
      // The city is not one of the form's fields — it lives in the picker — so a
      // rejection about it is put there by hand. Routing it through the form would
      // mark it handled and render it on a field that is not on the screen, which
      // suppresses the banner and shows the registrant nothing at all.
      if (err?.code === 'invalid_city') {
        setCityError(err.message);
        return;
      }
      // Field-level rejections land on their field; anything else needs the banner.
      if (!form.applyServerErrors(err, STUDENT_REGISTRATION_CODES)) setError(err);
    }
  });

  return (
    <AuthLayout wide>
      <form {...form.formProps} onSubmit={submit}>
        <Link to="/login" className="btn btn-ghost" style={{ marginBottom: 16, paddingLeft: 0 }}>
          <Icon name="back" size={15} />
          Back to sign in
        </Link>
        <h2>Create a student account</h2>
        <p className="sub">
          You can sign in straight away. Your library stays empty until you activate a book with the
          code from your teacher.
        </p>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        <div className="stack">
          <div className="grid-2">
            <Field label="First name" id="firstName" required error={form.errorFor('firstName')}>
              <input className="input" autoComplete="given-name" maxLength={v.LIMITS.firstName.max}
                     {...form.field('firstName')} />
            </Field>
            <Field label="Last name" id="lastName" error={form.errorFor('lastName')}>
              <input className="input" autoComplete="family-name" maxLength={v.LIMITS.lastName.max}
                     {...form.field('lastName')} />
            </Field>
          </div>

          <div className="grid-2">
            <Field
              label="Parent's mobile number"
              id="parentMobile"
              required
              hint="Verification codes are sent here"
              error={form.errorFor('parentMobile')}
            >
              <input className="input" {...v.mobileField(form, 'parentMobile')} />
            </Field>
            <Field
              label="Email address"
              id="email"
              required
              hint="This is how you sign in"
              error={form.errorFor('email')}
            >
              <input className="input" type="email" autoComplete="email" maxLength={255}
                     {...form.field('email')} />
            </Field>
          </div>

          <div className="grid-2">
            <Field label="Create password" id="password" required hint={v.PASSWORD_HINT}
                   error={form.errorFor('password')}>
              <input className="input" type="password" autoComplete="new-password" maxLength={100}
                     {...form.field('password')} />
            </Field>
            <Field label="Re-enter password" id="confirmPassword" required
                   error={form.errorFor('confirmPassword')}>
              <input className="input" type="password" autoComplete="new-password" maxLength={100}
                     {...form.field('confirmPassword')} />
            </Field>
          </div>

          <SchoolPicker value={school} onChange={setSchool} />

          <StateCityFields value={address} onChange={setAddress} cityError={cityError} />

          <div className="grid-2">
            <Field label="Area" id="area" error={form.errorFor('area')}>
              <input className="input" autoComplete="address-level3" maxLength={v.LIMITS.area.max}
                     {...form.field('area')} />
            </Field>
            <Field label="Postal code" id="postalCode" error={form.errorFor('postalCode')}>
              <input className="input" autoComplete="postal-code" {...v.postalField(form)} />
            </Field>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
            {form.submitting ? 'Creating…' : 'Create account'}
            <Icon name="arrow" size={15} />
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
